const LIGATURES = { œ: 'oe', æ: 'ae', ß: 'ss' };

// Folds one character to its searchable form: lowercase, no accents,
// punctuation (including ’ and ') turned into a space.
const foldChar = (ch) => {
  const lower = ch.toLowerCase();
  if (LIGATURES[lower]) return LIGATURES[lower];
  const base = lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return /^[a-z0-9&]+$/.test(base) ? base : ' ';
};

export const normalizeSearchText = (text = '') =>
  Array.from(String(text ?? ''))
    .map(foldChar)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

// Folded text plus, for every folded character, the index of the original
// character it comes from, so matches can be mapped back for highlighting.
const foldWithMap = (text) => {
  let folded = '';
  const map = [];
  Array.from(text).reduce((offset, ch) => {
    const f = foldChar(ch);
    for (let i = 0; i < f.length; i += 1) {
      folded += f[i];
      map.push(offset);
    }
    return offset + ch.length;
  }, 0);
  return { folded, map };
};

export const getSearchTokens = (query = '') =>
  normalizeSearchText(query).split(' ').filter(Boolean);

const isNumberToken = (token) => /^\d+$/.test(token);

export const getLyricsTexts = (cantique) => [
  ...(cantique?.couplets || []).map((c) => c?.text),
  cantique?.refrain?.text,
  cantique?.semiChoeur?.text,
].filter(Boolean);

// Words are matched from their start ("pres" finds "présence");
// a single letter must be a whole word ("ô" finds "Ô Jésus", not every "o").
const containsWord = (normalized, token) => (
  token.length === 1
    ? ` ${normalized} `.includes(` ${token} `)
    : ` ${normalized}`.includes(` ${token}`)
);

const isWordMatchAt = (folded, from, token) => {
  if (from > 0 && folded[from - 1] !== ' ') return false;
  if (token.length === 1) {
    const next = folded[from + 1];
    return next === undefined || next === ' ';
  }
  return true;
};

const findWord = (folded, token, from = 0) => {
  let index = folded.indexOf(token, from);
  while (index !== -1 && !isWordMatchAt(folded, index, token)) {
    index = folded.indexOf(token, index + 1);
  }
  return index;
};

// Match rank: lower is better. null means no match.
// Digit tokens match the hymn number (exact first, then prefix);
// word tokens must all be found in the title, the lyrics or, optionally, the language.
export const rankCantique = (cantique, tokens, { includeLanguage = false } = {}) => {
  if (!tokens.length) return 0;

  const numberText = cantique?.number != null ? String(cantique.number) : '';
  const title = normalizeSearchText(cantique?.title);
  const lyrics = normalizeSearchText(getLyricsTexts(cantique).join(' '));
  const langue = includeLanguage ? normalizeSearchText(cantique?.langue) : '';

  let exactNumber = false;
  let allInTitle = true;
  const words = [];

  for (const token of tokens) {
    if (isNumberToken(token)) {
      if (numberText === token) exactNumber = true;
      else if (!numberText.startsWith(token)) return null;
      continue;
    }
    words.push(token);
    const inTitle = containsWord(title, token);
    if (!inTitle && !containsWord(lyrics, token) && !(langue && containsWord(langue, token))) return null;
    if (!inTitle) allInTitle = false;
  }

  if (exactNumber) return 0;
  if (!words.length) return 1;
  const phrase = words.join(' ');
  if (` ${title}`.includes(` ${phrase}`)) return 2;
  if (allInTitle) return 3;
  if (` ${lyrics}`.includes(` ${phrase}`)) return 4;
  return 5;
};

export const searchCantiques = (cantiques, query, options) => {
  const tokens = getSearchTokens(query);
  if (!tokens.length) return cantiques;
  return cantiques
    .map((cantique, index) => ({ cantique, index, rank: rankCantique(cantique, tokens, options) }))
    .filter((entry) => entry.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.cantique);
};

const wordTokens = (query) => getSearchTokens(query).filter((t) => !isNumberToken(t));

// Splits `text` into [{ text, match }] parts for rendering highlights.
export const splitHighlight = (text = '', query = '') => {
  const source = String(text ?? '');
  const tokens = wordTokens(query);
  if (!source || !tokens.length) return [{ text: source, match: false }];

  const { folded, map } = foldWithMap(source);
  const ranges = [];
  tokens.forEach((token) => {
    let from = findWord(folded, token);
    while (from !== -1) {
      const end = from + token.length - 1;
      const endChar = map[end];
      const endLen = source.codePointAt(endChar) > 0xffff ? 2 : 1;
      ranges.push([map[from], endChar + endLen]);
      from = findWord(folded, token, from + token.length);
    }
  });
  if (!ranges.length) return [{ text: source, match: false }];

  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [ranges[0]];
  ranges.slice(1).forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  });

  const parts = [];
  let cursor = 0;
  merged.forEach(([s, e]) => {
    if (s > cursor) parts.push({ text: source.slice(cursor, s), match: false });
    parts.push({ text: source.slice(s, e), match: true });
    cursor = e;
  });
  if (cursor < source.length) parts.push({ text: source.slice(cursor), match: false });
  return parts;
};

// Short lyrics excerpt around the first match, used when the title itself does not match.
export const findLyricsExcerpt = (cantique, query, radius = 32) => {
  const tokens = wordTokens(query);
  if (!tokens.length) return null;
  const title = normalizeSearchText(cantique?.title);
  if (tokens.every((t) => containsWord(title, t))) return null;

  for (const raw of getLyricsTexts(cantique)) {
    const text = String(raw).replace(/\s+/g, ' ').trim();
    const { folded, map } = foldWithMap(text);
    const token = tokens.find((t) => !containsWord(title, t) && findWord(folded, t) !== -1)
      || tokens.find((t) => findWord(folded, t) !== -1);
    if (!token) continue;
    const pos = map[findWord(folded, token)];
    let start = Math.max(0, pos - radius);
    let end = Math.min(text.length, pos + token.length + radius);
    if (start > 0) {
      const space = text.indexOf(' ', start);
      if (space !== -1 && space < pos) start = space + 1;
    }
    if (end < text.length) {
      const space = text.lastIndexOf(' ', end);
      if (space > pos + token.length) end = space;
    }
    return `${start > 0 ? '… ' : ''}${text.slice(start, end)}${end < text.length ? ' …' : ''}`;
  }
  return null;
};

// Groups of cantiques sharing a number, or a title within the same language.
export const findDuplicateGroups = (cantiques) => {
  const byNumber = new Map();
  const byTitle = new Map();
  cantiques.forEach((c) => {
    if (c?.number != null) {
      const key = String(c.number);
      byNumber.set(key, [...(byNumber.get(key) || []), c]);
    }
    const title = normalizeSearchText(c?.title);
    if (title) {
      const key = `${normalizeSearchText(c?.langue)}|${title}`;
      byTitle.set(key, [...(byTitle.get(key) || []), c]);
    }
  });

  const groups = [];
  byNumber.forEach((items, key) => {
    if (items.length > 1) groups.push({ type: 'number', key: `number-${key}`, label: `N° ${key}`, items });
  });
  byTitle.forEach((items, key) => {
    if (items.length > 1) groups.push({ type: 'title', key: `title-${key}`, label: items[0].title.trim(), items });
  });
  return groups;
};
